import React, { useState } from 'react';
import { Card } from '@vehix/portal/components/ui/card';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@vehix/portal/lib/utils';
import type {
  AssetStatus,
  PerOemAsset,
  TenantAssetGroup
} from '@vehix/shared/types/recallSnapshots';

interface Props {
  groups: TenantAssetGroup[];
  isLoading?: boolean;
  onSelectAsset: (asset: PerOemAsset) => void;
}

const STATUS_PILL: Record<AssetStatus, string> = {
  healthy: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
  stale: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300'
};

function StatusPill({ status }: { status: AssetStatus }): React.ReactElement {
  return (
    <span
      className={cn(
        'inline-block rounded-full px-2 py-0.5 text-[11px] font-medium',
        STATUS_PILL[status]
      )}
    >
      {status}
    </span>
  );
}

function formatDelta(delta: number | null, pct: number | null): string {
  if (delta == null) return '—';
  const sign = delta > 0 ? '+' : '';
  const pctStr = pct != null ? ` (${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)` : '';
  return `${sign}${delta.toLocaleString()}${pctStr}`;
}

function relativeRefresh(at: string | null): string {
  if (!at) return '—';
  const ms = Date.now() - new Date(at).getTime();
  const hours = Math.round(ms / (60 * 60 * 1000));
  if (hours < 1) return '< 1h';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export const PerTenantAssetTable: React.FC<Props> = ({ groups, isLoading, onSelectAsset }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string): void => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading && groups.length === 0) {
    return (
      <Card className='p-4'>
        <div className='space-y-2'>
          {[0, 1, 2].map(i => (
            <div key={i} className='h-10 bg-muted animate-pulse rounded' />
          ))}
        </div>
      </Card>
    );
  }

  if (groups.length === 0) {
    return (
      <Card className='p-4'>
        <p className='text-sm text-muted-foreground py-3 text-center'>No tenants to display.</p>
      </Card>
    );
  }

  return (
    <Card className='overflow-hidden'>
      <div className='px-4 py-3 border-b'>
        <h3 className='text-sm font-semibold'>Per-tenant assets</h3>
        <p className='text-[11px] text-muted-foreground'>
          Click an OEM row for per-zone and per-data-collection-zone details
        </p>
      </div>
      <div className='divide-y'>
        {groups.map(group => {
          const isOpen = expanded.has(group.tenant_id);
          return (
            <div key={group.tenant_id}>
              {/* Tenant row */}
              <button
                type='button'
                onClick={() => toggle(group.tenant_id)}
                className='w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors'
              >
                {isOpen ? (
                  <ChevronDown className='h-4 w-4 text-muted-foreground flex-shrink-0' />
                ) : (
                  <ChevronRight className='h-4 w-4 text-muted-foreground flex-shrink-0' />
                )}
                <div className='min-w-0 flex-1'>
                  <p className='text-sm font-medium truncate'>{group.tenant_name}</p>
                  <p className='text-[11px] text-muted-foreground'>
                    {group.oems.length} OEM{group.oems.length === 1 ? '' : 's'}
                    {' · '}
                    last refresh {relativeRefresh(group.last_refresh_at)}
                  </p>
                </div>
                <StatusPill status={group.worst_status} />
              </button>

              {/* Per-OEM rows */}
              {isOpen && (
                <div className='bg-muted/20'>
                  {/* Column header */}
                  <div className='hidden sm:grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto] gap-3 px-4 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border-b'>
                    <span className='pl-7'>OEM</span>
                    <span className='text-right w-20'>Count</span>
                    {/* "As of", not "Snapshot" — it is the tenant-scoped crawl cutoff the
                        count was taken at, and it sits two columns from "Last refresh",
                        which is when the job ran. The two routinely differ by weeks. The
                        value is the newest across this OEM's zones, so the oldest zone in
                        the group is hidden; the detail drawer breaks it down per zone. */}
                    <span
                      className='text-right w-24'
                      title="Crawl cutoff the count was taken at, for this tenant — newest across the OEM's zones. Not when the refresh job ran; see Last refresh."
                    >
                      As of
                    </span>
                    <span className='text-right w-24'>Δ vs prev</span>
                    <span className='text-right w-16'>Adds</span>
                    <span className='text-right w-16'>Rems</span>
                    <span className='text-right w-20'>Last refresh</span>
                  </div>
                  {group.oems.map(oem => (
                    <button
                      key={oem.oem}
                      type='button'
                      onClick={() => onSelectAsset(oem)}
                      className='w-full text-left grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto] gap-3 items-center px-4 py-2 text-sm hover:bg-muted/40 transition-colors border-b last:border-b-0'
                    >
                      <span className='pl-7 flex items-center gap-2 min-w-0'>
                        <StatusPill status={oem.status} />
                        <span className='font-medium truncate'>{oem.oem}</span>
                      </span>
                      <span className='text-right w-20 tabular-nums'>
                        {oem.current_count.toLocaleString()}
                      </span>
                      <span className='text-right w-24 text-muted-foreground text-xs'>
                        {oem.latest_snapshot_date ?? '—'}
                      </span>
                      <span
                        className={cn(
                          'text-right w-24 tabular-nums text-xs',
                          oem.delta_count != null && oem.delta_count < 0 && 'text-red-600 dark:text-red-400',
                          oem.delta_count != null && oem.delta_count > 0 && 'text-emerald-600 dark:text-emerald-400'
                        )}
                      >
                        {formatDelta(oem.delta_count, oem.delta_pct)}
                      </span>
                      <span className='text-right w-16 tabular-nums text-xs text-emerald-600 dark:text-emerald-400'>
                        {oem.additions != null ? `+${oem.additions.toLocaleString()}` : '—'}
                      </span>
                      <span className='text-right w-16 tabular-nums text-xs text-red-600 dark:text-red-400'>
                        {oem.removals != null ? `-${oem.removals.toLocaleString()}` : '—'}
                      </span>
                      <span className='text-right w-20 text-muted-foreground text-xs'>
                        {relativeRefresh(oem.last_refresh_at)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default PerTenantAssetTable;
